/* Downloading what is on screen as a CSV.
 *
 * Built from the rows already fetched rather than asked of the API, so it works
 * without a second round trip and cannot disagree with what the person is
 * looking at. docs/plan.md also wants a download of a set larger than a page,
 * and that is a job rather than this, because a route that returns an unbounded
 * set is a defect. */

/* Characters Excel and Sheets read as the start of a formula.
 *
 * This matters here more than in most places. Company and person names in the
 * lake come from pages the engine fetched off the open web, so their content is
 * not ours, and the file is opened in Excel by the sales team. A name beginning
 * with one of these runs rather than displays. Prefixing a single quote makes
 * the spreadsheet treat it as text, which is the standard defence, and the quote
 * is not shown in the cell.
 *
 * Found by testing the writer rather than by reading it. */
const FORMULA_START = /^[=+\-@\t\r]/;

/** One CSV field, quoted only when it has to be, and never executable. */
function field(v: unknown): string {
  if (v === null || v === undefined) return "";
  let s = String(v);
  if (FORMULA_START.test(s)) s = "'" + s;
  // A comma, a quote, a newline or surrounding whitespace all change how a
  // reader parses the row, and Excel strips leading spaces from an unquoted
  // field.
  return /[",\n\r]|^\s|\s$/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function toCsv(columns: readonly string[], rows: readonly Record<string, unknown>[]): string {
  const lines = [columns.map(field).join(",")];
  for (const r of rows) lines.push(columns.map((c) => field(r[c])).join(","));
  // CRLF, because Excel on Windows treats a bare LF as one long line.
  return lines.join("\r\n");
}

export function downloadCsv(filename: string, csv: string): void {
  // The BOM is what makes Excel read this as UTF-8 rather than as the local
  // code page, which is how an accented company name becomes mojibake.
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** A filename somebody can find again: what it is, and when they took it. */
export function csvFilename(what: string): string {
  const d = new Date();
  const stamp = [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, "0"),
    String(d.getDate()).padStart(2, "0"),
  ].join("-");
  return `evie-${what}-${stamp}.csv`;
}
